import React, { useEffect, useState, useRef } from 'react';
import { X, Trash2, CheckCircle, Image, Download, Smartphone, Upload, Sparkles, Coins, ChevronDown } from 'lucide-react';
import { putObject } from '../utils/uploadToStorage';
import {
  fetchSkyboxStyles, generateSkyboxAndWait, getSkyboxCredits, SKYBOX_CREDITS, SkyboxError,
  type SkyboxQuality, type SkyboxStyle,
} from '../services/blockadeApi';
import { CheckoutModal } from './checkout/CheckoutModal';
import { generateHqSky, generateMuseSky, SKY_STYLES } from '../services/museSky';
import { listMySkies, saveToMyLibrary, type MySky } from '../services/myLibrary';
import { useWorldStore } from '../stores/worldStore';
import SkyboxThumbnail from './SkyboxThumbnail';
import './SkyboxManager.css';

// Skybox AI: a curated set of Blockade Model 4 styles, grouped. "More styles"
// shows every Model 4 style. Ids from GET /skybox/styles (2026-10); picked
// mostly from styles that ship a preview image (about half don't).
const CURATED_STYLE_GROUPS: { label: string; ids: number[] }[] = [
  { label: 'Realistic', ids: [227, 157, 198] },       // Cinematic Realism, Photorealistic, Freeform Photoreal
  { label: 'Skies', ids: [207, 156] },                // Above the Clouds, Sky Dome
  { label: 'Fantasy', ids: [226, 228, 158] },         // Fantasy Art, Storybook, Digital Painting
  { label: 'Sci-fi & space', ids: [224, 206, 205] },  // Deep Space, SciFi Concept, Cyberpunk Concept
  { label: 'Stylized', ids: [154, 159, 202] },        // Anime, Toon Cel, Low Poly Vector
  { label: 'Render', ids: [216, 155] },               // UHD Render, Open World
];
const DEFAULT_STYLE_ID = 227; // Cinematic Realism
/** A stable hue per style, for the tile of styles without a preview image. */
const styleHue = (name: string) => [...name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) % 360, 7);
const styleLabel = (name: string) => name.replace(/\s*\(M4\)\s*$/i, '');
const creditWord = (n: number) => `${n} credit${n === 1 ? '' : 's'}`;

interface Skybox {
  url: string;
  filename: string;
  uploadedAt: number;
  thumbnail?: string;
}

interface SkyboxManagerProps {
  skyboxes: { [id: string]: Skybox };
  currentSkyboxId: string | null;
  mobileSkyboxUrl: string | null;
  onClose: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onClearSkybox: () => void;
  onUpload: () => void;
  onMobileSkyboxChange: (url: string | null) => void;
  // A 3D sky: Blockade's mesh export is a panorama on a depth-displaced mesh,
  // so it gives parallax as you move rather than sitting at infinity. Placed as
  // a model flagged environmentMesh, not as a skybox image.
  onSkyMeshUpload?: (url: string, filename: string, mobileUrl?: string) => void;
  /** Show the high-quality 360° HDR option (admins until it's priced). */
  canHq?: boolean;
}

const SkyboxManager: React.FC<SkyboxManagerProps> = ({
  skyboxes,
  currentSkyboxId,
  mobileSkyboxUrl,
  onClose,
  onSelect,
  onDelete,
  onClearSkybox,
  onUpload,
  onMobileSkyboxChange,
  onSkyMeshUpload,
  canHq,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mobileUploading, setMobileUploading] = useState(false);
  const mobileFileRef = useRef<HTMLInputElement>(null);
  const skyMeshRef = useRef<HTMLInputElement>(null);
  const [meshUploading, setMeshUploading] = useState(false);
  const [meshStatus, setMeshStatus] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiStatus, setAiStatus] = useState<string | null>(null);
  // Muse (~1¢, ~30 s, pretty but painted) or Blockade Skybox AI (a true panorama model).
  const [aiEngine, setAiEngine] = useState<'muse' | 'hq' | 'blockade'>('muse');
  const [skyStyle, setSkyStyle] = useState(SKY_STYLES[0].id);
  // Skybox AI (paid with Studio credits): style, quality, negative text, balance.
  const [blStyles, setBlStyles] = useState<SkyboxStyle[] | null>(null);
  const [blStylesError, setBlStylesError] = useState(false);
  const [blStyleId, setBlStyleId] = useState(DEFAULT_STYLE_ID);
  const [blQuality, setBlQuality] = useState<SkyboxQuality>('standard');
  const [blNegative, setBlNegative] = useState('');
  const [blShowAll, setBlShowAll] = useState(false);
  const [credits, setCredits] = useState<{ admin: boolean; remaining: number; packUnits: number } | null | undefined>(undefined);
  const [blNotice, setBlNotice] = useState<{ text: string; buy?: boolean } | null>(null);
  const [buying, setBuying] = useState(false);
  const refreshCredits = () => { getSkyboxCredits().then(setCredits).catch(() => setCredits(null)); };
  useEffect(() => {
    if (aiEngine !== 'blockade') return;
    if (credits === undefined) refreshCredits();
    if (!blStyles) {
      fetchSkyboxStyles()
        .then((all) => setBlStyles(all.filter((st) => st.model === 'Model 4').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))))
        .catch(() => setBlStylesError(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiEngine]);
  const blStyle = blStyles?.find((st) => st.id === blStyleId);
  const blPromptMax = blStyle?.['max-char'] || 1600;
  const blNegativeMax = blStyle?.['negative-text-max-char'] || 400;
  const blCost = SKYBOX_CREDITS[blQuality];
  const blShort = !!credits && !credits.admin && credits.remaining < blCost;
  // The user's saved skies (any world): generated ones are filed there automatically.
  const [mySkies, setMySkies] = useState<MySky[]>([]);
  const refreshMySkies = () => { listMySkies().then(setMySkies).catch(() => {}); };
  useEffect(refreshMySkies, []);
  const worldSkyUrls = new Set(Object.values(skyboxes).map((s) => s.url));
  const savedSkies = mySkies.filter((s) => !worldSkyUrls.has(s.url));

  const addSavedSky = (sky: MySky) => {
    const store = useWorldStore.getState();
    const id = `skybox-lib-${Date.now()}`;
    store.addSkybox(id, { url: sky.url, filename: sky.name, uploadedAt: Date.now() });
    store.setCurrentSkyboxId(id);
  };

  const generateAiSkybox = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || aiStatus) return;
    setAiStatus('starting');
    try {
      if (aiEngine === 'muse' || aiEngine === 'hq') {
        const sky = aiEngine === 'hq'
          ? await generateHqSky(prompt, skyStyle, (s) => setAiStatus(s))
          : await generateMuseSky(prompt, skyStyle, (s) => setAiStatus(s));
        const store = useWorldStore.getState();
        const id = `skybox-ai-${Date.now()}`;
        store.addSkybox(id, { url: sky.url, filename: sky.filename, uploadedAt: Date.now() });
        store.setCurrentSkyboxId(id);
        setAiPrompt('');
        setAiStatus(null);
        setTimeout(refreshMySkies, 1500);
        return;
      }
      setBlNotice(null);
      if (blShort) {
        setAiStatus(null);
        setBlNotice({ text: `This sky needs ${creditWord(blCost)} and you have ${credits!.remaining}.`, buy: true });
        return;
      }
      const { fileUrl } = await generateSkyboxAndWait(
        prompt.slice(0, blPromptMax), blStyleId, (s) => setAiStatus(s),
        blNegative.trim().slice(0, blNegativeMax) || undefined, { quality: blQuality },
      );
      refreshCredits();
      // Persist to our own storage — Blockade URLs aren't permanent
      setAiStatus('saving');
      let url = fileUrl;
      try {
        const blob = await (await fetch(fileUrl)).blob();
        const key = `skyboxes/ai-${Date.now()}.jpg`;
        url = await putObject(key, blob, blob.type || 'image/jpeg');
      } catch (persistErr) {
        console.warn('[SkyboxManager] AI skybox S3 persist failed, using Blockade URL:', persistErr);
      }
      const store = useWorldStore.getState();
      const id = `skybox-ai-${Date.now()}`;
      store.addSkybox(id, { url, filename: `AI: ${prompt.slice(0, 40)}`, uploadedAt: Date.now() });
      if (url !== fileUrl) saveToMyLibrary({ type: 'skybox', name: `AI: ${prompt.slice(0, 40)}`, url, source: 'sky-blockade' }).then(refreshMySkies);
      store.setCurrentSkyboxId(id);
      setAiPrompt('');
      setAiStatus(null);
    } catch (err: any) {
      console.error('[SkyboxManager] AI skybox failed:', err);
      setAiStatus(null);
      if (aiEngine === 'blockade') {
        // Credits are refunded server-side when a generation fails.
        refreshCredits();
        if (err instanceof SkyboxError && err.needsCredits) setBlNotice({ text: err.message, buy: true });
        else if (err instanceof SkyboxError && err.capReached) setBlNotice({ text: err.message });
        else setBlNotice({ text: `${err?.message || 'Skybox generation failed.'} Any credits it used were returned.` });
        return;
      }
      alert(err?.message || 'Skybox generation failed. Please try again.');
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getFileSize = (url: string) => {
    // Since we don't have file size info, we'll show "HDR" for .hdr files
    if (url.includes('.hdr')) return 'HDR';
    if (url.includes('.exr')) return 'EXR';
    return 'Image';
  };

  const engines = [
    { id: 'muse' as const, label: 'Muse', note: 'Quick painted sky' },
    ...(canHq ? [{ id: 'hq' as const, label: 'HQ 360 HDR', note: 'True 360°, 4K, real light' }] : []),
    { id: 'blockade' as const, label: 'Skybox AI', note: 'Blockade panorama · 1–2 credits' },
  ];

  return (
    <div className="skybox-manager-overlay" onClick={onClose}>
      <div className="skybox-manager" role="dialog" aria-label="Sky" onClick={(e) => e.stopPropagation()}>
        <div className="skybox-manager-header">
          <h2>Sky</h2>
          <button className="skybox-manager-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="sb-body">
          <section className="sb-section sb-generate">
            <div className="sb-section-head"><h3><Sparkles size={15} /> Generate a sky</h3></div>
            <div className="sb-engines" role="radiogroup" aria-label="Sky generator">
              {engines.map((en) => (
                <button key={en.id} type="button" role="radio" aria-checked={aiEngine === en.id}
                  className={`sb-engine ${aiEngine === en.id ? 'on' : ''}`}
                  onClick={() => setAiEngine(en.id)} disabled={!!aiStatus}>
                  <span className="sb-engine-name">{en.label}</span>
                  <span className="sb-engine-note">{en.note}</span>
                </button>
              ))}
            </div>
            {aiEngine !== 'blockade' && (
              <div className="sb-styles" role="radiogroup" aria-label="Style">
                <span className="sb-label">Style</span>
                {SKY_STYLES.map((st) => (
                  <button key={st.id} type="button" role="radio" aria-checked={skyStyle === st.id}
                    className={`sb-chip ${skyStyle === st.id ? 'on' : ''}`}
                    onClick={() => setSkyStyle(st.id)} disabled={!!aiStatus}>{st.label}</button>
                ))}
              </div>
            )}
            {aiEngine === 'blockade' && (
              <div className="sb-bl">
                <div className="sb-bl-head">
                  <span className="sb-label">Style{blStyle ? <> · <b>{styleLabel(blStyle.name)}</b></> : null}</span>
                  {blStyles && (
                    <button type="button" className="sb-bl-more" aria-expanded={blShowAll} onClick={() => setBlShowAll((v) => !v)}>
                      {blShowAll ? 'Featured styles' : `More styles (${blStyles.length})`}
                      <ChevronDown size={14} className={blShowAll ? 'up' : ''} aria-hidden />
                    </button>
                  )}
                </div>
                {blStylesError && <p className="sb-muted">Couldn't load styles. Freeform Photoreal will be used.</p>}
                {!blStyles && !blStylesError && <p className="sb-muted">Loading styles…</p>}
                {blStyles && (
                  <div className={`sb-bl-groups ${blShowAll ? 'all' : ''}`} role="radiogroup" aria-label="Skybox AI style">
                    {(blShowAll
                      ? [{ label: 'All Model 4 styles', items: blStyles }]
                      : CURATED_STYLE_GROUPS.map((g) => ({
                          label: g.label,
                          items: g.ids.map((id) => blStyles.find((st) => st.id === id)).filter(Boolean) as SkyboxStyle[],
                        })).filter((g) => g.items.length)
                    ).map((g) => (
                      <div key={g.label} className="sb-bl-group">
                        <div className="sb-bl-group-label">{g.label}</div>
                        <div className="sb-bl-cards">
                          {g.items.map((st) => (
                            <button key={st.id} type="button" role="radio" aria-checked={blStyleId === st.id}
                              className={`sb-bl-card ${blStyleId === st.id ? 'on' : ''}`}
                              onClick={() => setBlStyleId(st.id)} disabled={!!aiStatus}
                              title={st.description || styleLabel(st.name)}>
                              {st.image_jpg
                                ? <img src={st.image_jpg} alt="" loading="lazy" decoding="async" />
                                : <span className="sb-bl-card-ph" aria-hidden style={{ '--sb-ph-h': styleHue(st.name) } as React.CSSProperties} />}
                              <span className="sb-bl-card-name">{styleLabel(st.name)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="sb-bl-row">
                  <div className="sb-bl-quality" role="radiogroup" aria-label="Quality">
                    {(['standard', 'enhanced'] as const).map((q) => (
                      <button key={q} type="button" role="radio" aria-checked={blQuality === q}
                        className={`sb-chip ${blQuality === q ? 'on' : ''}`}
                        onClick={() => setBlQuality(q)} disabled={!!aiStatus}>
                        {q === 'standard' ? 'Standard' : 'Enhanced'} · {creditWord(SKYBOX_CREDITS[q])}
                      </button>
                    ))}
                  </div>
                  <span className="sb-bl-balance">
                    <Coins size={14} aria-hidden />
                    {credits === undefined ? '…'
                      : credits === null ? 'Sign in to use Skybox AI'
                      : credits.admin ? 'Admin · unlimited'
                      : `You have ${creditWord(credits.remaining)}`}
                    {credits && !credits.admin && (
                      <button type="button" className="sb-bl-buy" onClick={() => setBuying(true)}>Get more</button>
                    )}
                  </span>
                </div>
                <input
                  className="sb-bl-negative"
                  type="text"
                  value={blNegative}
                  maxLength={blNegativeMax}
                  placeholder="Leave out (optional)… e.g. people, text, buildings"
                  onChange={(e) => setBlNegative(e.target.value)}
                  disabled={!!aiStatus}
                  aria-label="Leave out of the sky"
                />
              </div>
            )}
            <div className="sb-prompt">
              <input
                type="text"
                value={aiPrompt}
                maxLength={aiEngine === 'blockade' ? blPromptMax : undefined}
                placeholder="Describe the place… e.g. city rooftop at golden hour"
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && generateAiSkybox()}
                disabled={!!aiStatus}
                aria-label="Describe the sky"
              />
              <button className="sb-btn sb-btn-primary" onClick={generateAiSkybox}
                disabled={!!aiStatus || !aiPrompt.trim() || (aiEngine === 'blockade' && credits === null)}>
                <Sparkles size={15} />
                {aiStatus ? `${aiStatus[0].toUpperCase()}${aiStatus.slice(1)}…`
                  : aiEngine === 'blockade' ? `Generate · ${creditWord(blCost)}` : 'Generate'}
              </button>
            </div>
            {aiEngine === 'blockade' && blNotice && (
              <div className="sb-bl-notice" role="alert">
                <span>{blNotice.text}</span>
                {blNotice.buy && (
                  <button type="button" className="sb-btn sb-btn-primary" onClick={() => setBuying(true)}>
                    <Coins size={14} aria-hidden /> Get {credits?.packUnits ?? 6} credits · $9.99
                  </button>
                )}
              </div>
            )}
            {buying && (
              <CheckoutModal
                product="studio"
                returnPath={typeof window !== 'undefined' ? window.location.pathname : '/dashboard'}
                onClose={() => setBuying(false)}
                onPaid={() => { setBuying(false); setBlNotice(null); refreshCredits(); }}
              />
            )}
          </section>

          <section className="sb-section">
            <div className="sb-section-head">
              <h3>In this world</h3>
              <div className="sb-head-actions">
                <button className="sb-btn" onClick={onUpload}><Upload size={14} /> Upload image</button>
                <button className="sb-btn" onClick={onClearSkybox} disabled={!currentSkyboxId}>Use default sky</button>
              </div>
            </div>
            <div className="skybox-manager-grid">
              {Object.keys(skyboxes).length === 0 ? (
                <div className="skybox-manager-empty">
                  <Image size={48} />
                  <p>No skyboxes uploaded yet</p>
                  <p className="skybox-manager-hint">
                    Upload a panoramic image or generate an AI sky above
                  </p>
                </div>
              ) : (
                Object.entries(skyboxes).map(([id, skybox]) => (
                  <div
                    key={id}
                    className={`skybox-item ${currentSkyboxId === id ? 'active' : ''}`}
                    onMouseEnter={() => setHoveredId(id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => onSelect(id)}
                  >
                    <div className="skybox-thumbnail">
                      <SkyboxThumbnail 
                        url={skybox.url} 
                        width={240} 
                        height={160}
                        onThumbnailGenerated={(dataUrl) => {
                          // Optional: Could save thumbnail to state here
                        }}
                      />
                      {currentSkyboxId === id && (
                        <div className="skybox-active-indicator">
                          <CheckCircle size={24} />
                        </div>
                      )}
                    </div>

                    <div className="skybox-info">
                      <div className="skybox-name">{skybox.filename}</div>
                      <div className="skybox-meta">
                        <span className="skybox-type">{getFileSize(skybox.url)}</span>
                        <span className="skybox-date">{formatDate(skybox.uploadedAt)}</span>
                      </div>
                    </div>

                    <div className="skybox-actions">
                      <a
                        href={skybox.url}
                        download={skybox.filename}
                        className="skybox-action-btn"
                        onClick={(e) => e.stopPropagation()}
                        title="Download"
                      >
                        <Download size={14} />
                      </a>
                      <button
                        className="skybox-action-btn skybox-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this skybox?')) {
                            onDelete(id);
                          }
                        }}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {savedSkies.length > 0 && (
            <section className="sb-section skybox-library">
              <div className="sb-section-head"><h3>Your skies</h3><span className="sb-muted">From your other worlds. Click one to use it here.</span></div>
              <div className="skybox-library-row">
                {savedSkies.map((sky) => (
                  <button key={sky.id} type="button" className="skybox-library-item" onClick={() => addSavedSky(sky)} title={sky.name}>
                    <SkyboxThumbnail url={sky.url} width={160} height={90} />
                    <span>{sky.name}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="sb-section sb-advanced">
            <div className="sb-section-head"><h3>More options</h3></div>
            <div className="sb-adv-row">
              <div className="sb-adv-text">
                <strong><Smartphone size={14} /> Phone &amp; headset sky</strong>
                <span>A lighter jpg or png for phones and Quest, which can't load heavy HDR files.</span>
              </div>
              {mobileSkyboxUrl ? (
                <div className="sb-adv-preview">
                  <img src={mobileSkyboxUrl} alt="" />
                  <button className="sb-btn sb-btn-danger" onClick={() => onMobileSkyboxChange(null)}><Trash2 size={14} /> Remove</button>
                </div>
              ) : (
                <button className="sb-btn" onClick={() => mobileFileRef.current?.click()} disabled={mobileUploading}>
                  <Upload size={14} /> {mobileUploading ? 'Uploading…' : 'Upload'}
                </button>
              )}
            </div>
            {onSkyMeshUpload && (
              <div className="sb-adv-row">
                <div className="sb-adv-text">
                  <strong>3D sky (GLB)</strong>
                  <span>A sky mesh with real depth, so it shifts as you move.</span>
                </div>
                <button className="sb-btn" onClick={() => skyMeshRef.current?.click()} disabled={meshUploading}>
                  <Upload size={14} /> {meshUploading ? (meshStatus || 'Uploading…') : 'Upload GLB'}
                </button>
              </div>
            )}
          </section>
        </div>

        <div className="sb-hidden-inputs">
          <input
            ref={skyMeshRef}
            type="file"
            accept=".glb,.gltf"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file || !onSkyMeshUpload) return;
              setMeshUploading(true);
              try {
                const stamp = Date.now();
                const put = async (body: Blob | File, suffix: string) => {
                  const key = `skyboxes/mesh-${stamp}${suffix}-${file.name}`;
                  return putObject(key, body, 'model/gltf-binary');
                };

                setMeshStatus('Uploading…');
                const url = await put(file, '');

                // Quest and phones cap textures at 4096px — above that they
                // fail to upload entirely and the sky renders white. Build a
                // second variant so those devices get something to look at.
                let mobileUrl: string | undefined;
                try {
                  const { downscaleGlbTextures } = await import('../utils/downscaleGlb');
                  const result = await downscaleGlbTextures(await file.arrayBuffer(), 4096, setMeshStatus);
                  if (result) {
                    setMeshStatus('Uploading mobile version…');
                    mobileUrl = await put(result.blob, '-mobile');
                  }
                } catch (dsErr) {
                  // Desktop still works without it — never fail the whole upload.
                  console.warn('[SkyboxManager] Mobile variant failed, desktop only:', dsErr);
                }
                onSkyMeshUpload(url, file.name, mobileUrl);
              } catch (err) {
                console.error('[SkyboxManager] Sky mesh upload failed:', err);
                alert('Upload failed. Please try again.');
              } finally {
                setMeshUploading(false);
                setMeshStatus('');
                if (skyMeshRef.current) skyMeshRef.current.value = '';
              }
            }}
          />
          <input
            ref={mobileFileRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setMobileUploading(true);
              try {
                const key = `skyboxes/mobile-${Date.now()}-${file.name}`;
                const url = await putObject(key, file, file.type || 'image/jpeg');
                onMobileSkyboxChange(url);
              } catch (err) {
                console.error('[SkyboxManager] Mobile skybox upload failed:', err);
                alert('Upload failed. Please try again.');
              } finally {
                setMobileUploading(false);
                if (mobileFileRef.current) mobileFileRef.current.value = '';
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SkyboxManager;